import { useCallback, useEffect, useRef, useState } from 'react';
import {
  browserOffline,
  functionHeaders,
  cacheKey,
  cacheRows,
  cachedRows,
  getDb,
  listChecklistItems,
  listChecklists,
  listHouses,
  listOpenRequests,
  listRooms,
  listStaysPublic,
  loadPower,
  pendingOps,
  type Asset,
  type ChecklistRun,
  type Message,
  type StaffDirectoryRow,
  type Stay,
} from '@dia/db';
import { supabase } from '../../supabase';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '../../env';
import {
  applyPendingToRun,
  mergePendingRunItems,
  type PendingOp,
  type RunItemLike,
} from './checklist';

/**
 * Reads for the ops screens. Network first, the phone's own copy second, and
 * never a throw for being offline (Section 11). Writes are not here: they all
 * go through useOutbox() in the screen that makes them.
 */

type Loaded = { data: unknown; error: { message: string } | null };

/**
 * The phone's own answer to "is there any point trying" (defect D6).
 *
 * When the browser says it is offline, a fetch to the island does not fail
 * fast: Chromium spends about seven seconds on DNS and connection attempts
 * before it gives up. Ticking a card reloads the run and its items, so every
 * tick froze the button for those seven seconds. On a 293 card cleaning that is
 * over half an hour of a crew member staring at a dead screen, which is exactly
 * how an offline first app loses the people it was built for.
 *
 * navigator.onLine is a weak signal in general (connected to island Wi-Fi with
 * the satellite down still reads as online), but it is never wrong in the
 * direction that matters here: false means there is no network at all, so the
 * cache is the only possible answer and asking costs seven seconds to learn it.
 */
async function readThrough<T extends { id: string }>(entity: string, load: () => PromiseLike<Loaded>): Promise<T[]> {
  if (browserOffline()) return cachedRows<T>(entity);
  try {
    const { data, error } = await load();
    if (error) throw new Error(error.message);
    if (Array.isArray(data)) return cacheRows(entity, data as T[]);
  } catch {
    // Offline is normal here. Fall through to what the phone already has.
  }
  return cachedRows<T>(entity);
}

export function loadAssets(): Promise<Asset[]> {
  return readThrough<Asset>('assets', () => supabase.from('assets').select('*').eq('active', true).order('sort'));
}

export function loadStaffDirectory(): Promise<StaffDirectoryRow[]> {
  return readThrough<StaffDirectoryRow>('staff_directory', () => supabase.from('staff_directory').select('*'));
}

/** The full stay rows. Managers and owners only: the database refuses everyone else. */
export function loadStays(): Promise<Stay[]> {
  return readThrough<Stay>('stays', () => supabase.from('stays').select('*').order('arrives_on', { ascending: false }).limit(60));
}

/** Server runs, plus the ones this phone started that the island has not confirmed yet. */
export async function loadRuns(): Promise<ChecklistRun[]> {
  const server = await readThrough<ChecklistRun>('checklist_runs', () =>
    supabase.from('checklist_runs').select('*').order('created_at', { ascending: false }).limit(60),
  );
  const ops = (await pendingOps()) as PendingOp[];
  const known = new Set(server.map((r) => r.id));
  const merged = server.map((r) => applyPendingToRun(r, ops, r.id) ?? r);
  for (const op of ops) {
    if (op.table !== 'checklist_runs' || op.kind !== 'insert') continue;
    const id = String(op.payload.id ?? '');
    if (!id || known.has(id)) continue;
    known.add(id);
    const run = applyPendingToRun(null, ops, id);
    if (run) merged.unshift(run);
  }
  return merged.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export async function loadRun(runId: string): Promise<ChecklistRun | null> {
  const [runs, ops] = await Promise.all([loadRuns(), pendingOps()]);
  const server = runs.find((r) => r.id === runId) ?? null;
  return applyPendingToRun(server, ops as PendingOp[], runId);
}

/** Server rows plus the ones still in the outbox. */
export async function loadRunItems(runId: string): Promise<RunItemLike[]> {
  const entity = `run_items:${runId}`;
  const server = await readThrough<RunItemLike & { id: string }>(entity, () =>
    supabase.from('run_items').select('*').eq('run_id', runId).order('done_at'),
  );
  return mergePendingRunItems(server, (await pendingOps()) as PendingOp[], runId);
}

export type ThreadKind = 'request' | 'stay';

/** A thread is one request (staff talking about a job) or one stay (hosts and the guest). */
export async function loadMessages(threadId: string, kind: ThreadKind = 'request'): Promise<Message[]> {
  const column = kind === 'request' ? 'request_id' : 'stay_id';
  const server = await readThrough<Message>(`messages:${kind}:${threadId}`, () =>
    supabase.from('messages').select('*').eq('thread_kind', kind).eq(column, threadId).order('created_at'),
  );
  const seen = new Set(server.map((m) => m.client_id));
  const pending: Message[] = [];
  for (const op of (await pendingOps()) as PendingOp[]) {
    if (op.table !== 'messages' || op.kind !== 'insert') continue;
    if (op.payload.thread_kind !== kind || op.payload[column] !== threadId) continue;
    const clientId = String(op.payload.client_id ?? op.id ?? '');
    if (!clientId || seen.has(clientId)) continue;
    pending.push({
      id: clientId,
      thread_kind: kind,
      stay_id: kind === 'stay' ? threadId : null,
      // A stay message may name the request it answers (migration 022).
      request_id: kind === 'request' ? threadId : ((op.payload.request_id as string | null) ?? null),
      author_kind: 'staff',
      author_staff_id: (op.payload.author_staff_id as string | null) ?? null,
      author_guest_id: null,
      text_original: String(op.payload.text_original ?? ''),
      lang_original: (op.payload.lang_original as 'es' | 'en') ?? 'es',
      text_es: (op.payload.text_es as string | null) ?? null,
      text_en: (op.payload.text_en as string | null) ?? null,
      client_id: clientId,
      created_at: String(op.payload.created_at ?? new Date().toISOString()),
    });
  }
  return [...server, ...pending].sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/** Everything a Limpieza job needs, read once and kept on the phone. */
export async function loadJobCatalog() {
  const [houses, rooms, checklists] = await Promise.all([
    listHouses(supabase),
    listRooms(supabase),
    listChecklists(supabase),
  ]);
  return { houses, rooms, checklists };
}

export function loadOpenRequests() {
  return listOpenRequests(supabase);
}

export function loadStaysToday() {
  return listStaysPublic(supabase);
}

export function loadItems(checklistId: string) {
  return listChecklistItems(supabase, checklistId);
}

/** A tiny loader hook: run now, again every so often, on reconnect, and when the screen comes back. */
export function useLoad<T>(loader: () => Promise<T>, key: string, pollMs = 0) {
  const latest = useRef(loader);
  latest.current = loader;
  const [state, setState] = useState<{ data: T | null; loading: boolean }>({ data: null, loading: true });

  const reload = useCallback(async () => {
    try {
      const data = await latest.current();
      setState({ data, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    void reload();
    const onWake = () => {
      if (document.visibilityState !== 'hidden') void reload();
    };
    window.addEventListener('online', onWake);
    document.addEventListener('visibilitychange', onWake);
    const timer = pollMs > 0 ? window.setInterval(() => void reload(), pollMs) : null;
    return () => {
      window.removeEventListener('online', onWake);
      document.removeEventListener('visibilitychange', onWake);
      if (timer !== null) window.clearInterval(timer);
    };
  }, [key, pollMs, reload]);

  return { ...state, reload };
}

/* ------------------------------------------------------------------ */
/* Reference photos: "this is how it should look"                      */
/* ------------------------------------------------------------------ */

/**
 * Signed URLs change on every request, so the browser cache cannot hold them.
 * The photo itself is kept in IndexedDB instead, keyed by its storage path, so
 * a reference photo that was seen once (or precached) opens with no signal.
 */
export async function referencePhotoBlob(path: string): Promise<Blob | null> {
  const db = getDb();
  const key = cacheKey('refphoto', path);
  const hit = await db.cache.get(key);
  if (hit && hit.row instanceof Blob) return hit.row;

  try {
    const { data, error } = await supabase.storage.from('reference-photos').download(path);
    if (error || !data) return null;
    await db.cache.put({ key, entity: 'refphoto', id: path, row: data, cachedAt: Date.now() });
    return data;
  } catch {
    return null;
  }
}

export function useReferencePhoto(path: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    let made: string | null = null;
    void referencePhotoBlob(path).then((blob) => {
      if (!alive || !blob) return;
      made = URL.createObjectURL(blob);
      setUrl(made);
    });
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [path]);
  return path ? url : null;
}

/* ------------------------------------------------------------------ */
/* Precache                                                            */
/* ------------------------------------------------------------------ */

let precacheStarted = false;

/**
 * After sign in, pull down everything the crew reads with the network off:
 * houses, rooms, every checklist with its items, assets, the team, the queue,
 * and the reference photos. Content pages are bundled with the app and cached
 * by the service worker, so they need nothing here.
 */
export async function precacheOps(): Promise<void> {
  if (precacheStarted || typeof navigator !== 'undefined' && navigator.onLine === false) return;
  precacheStarted = true;
  try {
    const [{ checklists }] = await Promise.all([
      loadJobCatalog(),
      loadAssets(),
      loadStaffDirectory(),
      loadOpenRequests(),
      loadStaysToday(),
      loadRuns(),
      loadPower(supabase),
    ]);

    const paths = new Set<string>();
    for (const checklist of checklists) {
      const items = await loadItems(checklist.id);
      for (const item of items) if (item.reference_photo_path) paths.add(item.reference_photo_path);
    }

    const queue = [...paths];
    const worker = async () => {
      for (let path = queue.pop(); path; path = queue.pop()) await referencePhotoBlob(path);
    };
    await Promise.all([worker(), worker(), worker()]);
  } catch {
    // A failed precache is not an error the crew should see. Try next time.
    precacheStarted = false;
  }
}

/* ------------------------------------------------------------------ */
/* translate                                                           */
/* ------------------------------------------------------------------ */

export type TranslateResult =
  | { ok: true; text: string; cached: boolean }
  | { ok: false; reason: 'offline' | 'quota' | 'session' | 'failed'; message_es: string; message_en: string };

/**
 * One call to the translate function (Section 10). Never throws. A caller that
 * is sending a message treats a failure as "send it anyway in its own
 * language", which is what the function's own error text promises.
 */
export async function translateText(
  text: string,
  source: 'es' | 'en',
  target: 'es' | 'en',
  token: string,
  audience: 'staff' | 'owner' | 'guest' = 'staff',
  timeoutMs = 8000,
): Promise<TranslateResult> {
  if (source === target) return { ok: true, text, cached: true };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return {
      ok: false,
      reason: 'offline',
      message_es: 'No hay señal. La traducción sale cuando vuelva.',
      message_en: 'No signal. The translation waits for the signal.',
    };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/translate`, {
      method: 'POST',
      signal: controller.signal,
      headers: functionHeaders({ anonKey: SUPABASE_ANON_KEY, token }),
      body: JSON.stringify({ text, source_lang: source, target_lang: target, audience }),
    });
    const body = (await response.json()) as Record<string, unknown>;
    if (response.ok && typeof body.text === 'string') {
      return { ok: true, text: body.text, cached: body.cached === true };
    }
    const reason = response.status === 429 ? 'quota' : response.status === 401 ? 'session' : 'failed';
    return {
      ok: false,
      reason,
      message_es: String(body.message_es ?? 'No se pudo traducir ahora.'),
      message_en: String(body.message_en ?? 'The translation did not work just now.'),
    };
  } catch {
    return {
      ok: false,
      reason: 'failed',
      message_es: 'No se pudo traducir ahora.',
      message_en: 'The translation did not work just now.',
    };
  } finally {
    clearTimeout(timer);
  }
}

/* ------------------------------------------------------------------ */
/* Weather, one line                                                   */
/* ------------------------------------------------------------------ */

/**
 * Approximate position of Isla Solarte. The brief does not give the island's
 * own coordinates, so this is a placeholder for a forecast line only, listed in
 * content/ops/PLACEHOLDERS.md. Phase 3 replaces it with the island's readings.
 */
const FORECAST_POINT = { lat: 9.32, lon: -82.2 };
const WEATHER_KEY = 'dia.ops.weather';

export type StoredWeather = { tempC: number; code: number; windKph: number; at: string };

export function readStoredWeather(): StoredWeather | null {
  try {
    const raw = globalThis.localStorage?.getItem(WEATHER_KEY);
    return raw ? (JSON.parse(raw) as StoredWeather) : null;
  } catch {
    return null;
  }
}

export async function loadWeather(): Promise<StoredWeather | null> {
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${FORECAST_POINT.lat}&longitude=${FORECAST_POINT.lon}` +
      '&current=temperature_2m,weather_code,wind_speed_10m&timezone=America%2FPanama';
    const response = await fetch(url);
    if (!response.ok) throw new Error('weather');
    const body = (await response.json()) as {
      current?: { temperature_2m: number; weather_code: number; wind_speed_10m: number; time: string };
    };
    if (!body.current) throw new Error('weather');
    const next: StoredWeather = {
      tempC: body.current.temperature_2m,
      code: body.current.weather_code,
      windKph: body.current.wind_speed_10m,
      at: new Date().toISOString(),
    };
    try {
      globalThis.localStorage?.setItem(WEATHER_KEY, JSON.stringify(next));
    } catch {
      // Not stored. It will be fetched again next time.
    }
    return next;
  } catch {
    return readStoredWeather();
  }
}
